import React from 'react';
import {useParams, Link} from "react-router-dom";
import useGet from "../../components/hooks/useGet";
import ArchiveToggleButton from "../../components/admin/ArchiveToggleButton";
import {Course, CourseGroup, Deletable, User, UserAssignment, UserAssignmentType} from "fearless-common";
import {DataRow, Pages} from "../../components/layout";
import {adminRoutes} from "../../utils/routes";
import {backendEndpoints} from "../../libs/backendEndpoints";
import Loading from "../../components/Loading";
import UrlTabs from "../../components/UrlTabs";
import moment from "moment";
import {defaultDateTimeFormat} from "../../utils/date";
import Table from "react-bootstrap/Table";
import {getFullName, useTitle} from "fearless-common-ui";
import {concepts} from "fearless-common";
import {BoxArrowUpRight} from "react-bootstrap-icons";

/** A {@link CourseGroup} with its enrolled users eagerly loaded. */
type CourseGroupWithUsers = CourseGroup & { users: (User & { pivot: { role: string } })[] };

/** A {@link Course} with soft-delete fields, student groups (including users), and educator assignments loaded. */
type CourseWithGroups = Course & Deletable & {
    groups: CourseGroupWithUsers[];
    user_assignments: (UserAssignment & { user: User })[];
};

/**
 * Admin detail page for a single ELN course.
 *
 * Fetches the course together with its student groups and their users, then
 * presents two URL-driven tabs:
 *
 * - **Details** — read-only metadata (name, description, dates, grading,
 *   group settings, and soft-delete info).
 * - **Students** — a deduplicated table of every enrolled student across all
 *   groups, showing their name, email, and the group they belong to. Each
 *   student ID links to the corresponding {@link AdminUser} page.
 * - **Educators** — table of all user assignments (course admins and teaching
 *   assistants), each linking to the corresponding {@link AdminUser} page.
 * - **Customer Service** — archive the course (soft-delete) or restore it if
 *   already archived.
 *
 * URL pattern: `/admin/courses/:courseId/:tab?`
 */
/** Maps a {@link UserAssignmentType} to a human-readable role label. */
const roleLabel: Record<string, string> = {
    [UserAssignmentType.COURSE_ADMINISTRATOR]: 'Course admin',
    [UserAssignmentType.COURSE_TEACHING_ASSISTANT]: 'Teaching assistant',
};

export default function AdminCourse() {
    const { courseId } = useParams() as { courseId: string };
    const { data: course, isLoading, reload } = useGet<CourseWithGroups>({
        endpoint: backendEndpoints.getAdminCourse(courseId),
        params: {
            _with: ['groups.users'],
        }
    });

    useTitle('Admin Course');

    const students = React.useMemo(() => {
        if (!course?.groups) return [];
        const seen = new Set<number>();
        const result: Array<{ user: User; groupName: string }> = [];
        for (const group of course.groups) {
            for (const user of group.users ?? []) {
                if (!seen.has(user.id)) {
                    seen.add(user.id);
                    result.push({ user, groupName: group.name });
                }
            }
        }
        return result;
    }, [course]);

    return (
        <Pages.Centred>
            <Pages.BackButton href={adminRoutes.courses}>Courses</Pages.BackButton>
            <h1>Course: {course?.name}</h1>
            <div className="d-flex gap-4 mb-3">
                {course?.deleted_at && (
                    <span>Archived at: {moment(course.deleted_at).format(defaultDateTimeFormat)}</span>
                )}
                <span>Created: {moment(course?.created_at).format(defaultDateTimeFormat)}</span>
                <span>Last updated: {moment(course?.updated_at).format(defaultDateTimeFormat)}</span>
            </div>
            {isLoading && <Loading />}
            <UrlTabs
                baseUrl={(k) => adminRoutes.course(courseId, k)}
                queryParameter="tab"
                defaultActiveKey="details"
            >
                <UrlTabs.Tab title="Details" eventKey="details">
                    <UrlTabs.TabContent>
                        {course && (
                            <>
                                <DataRow label="ID">{course.id}</DataRow>
                                <DataRow label="Name">{course.name || <i className="text-muted">None</i>}</DataRow>
                                <DataRow label="Description">{course.description || <i className="text-muted">None</i>}</DataRow>
                                <DataRow label="Starts at">
                                    {course.starts_at ? moment(course.starts_at).format(defaultDateTimeFormat) : <i className="text-muted">None</i>}
                                </DataRow>
                                <DataRow label="Deadline">
                                    {course.deadline ? moment(course.deadline).format(defaultDateTimeFormat) : <i className="text-muted">None</i>}
                                </DataRow>
                                <DataRow label="Grading scheme">{course.grading_scheme || <i className="text-muted">None</i>}</DataRow>
                                <DataRow label="Max groups">{course.max_number_of_groups ?? <i className="text-muted">Unlimited</i>}</DataRow>
                                <DataRow label="Max students per group">{course.max_students_per_group ?? <i className="text-muted">Unlimited</i>}</DataRow>
                                <DataRow label="Students work individually">{course.groups_are_individuals === null ? <i className="text-muted">Not set</i> : course.groups_are_individuals ? 'Yes' : 'No'}</DataRow>
                                <DataRow label="Deleted at">{course.deleted_at ? moment(course.deleted_at).format(defaultDateTimeFormat) : <i className="text-muted">Not archived</i>}</DataRow>
                            </>
                        )}
                    </UrlTabs.TabContent>
                </UrlTabs.Tab>
                <UrlTabs.Tab title={`Students (${students.length})`} eventKey="students">
                    <UrlTabs.TabContent>
                        {students.length === 0 ? (
                            <p className="text-muted">No students enrolled.</p>
                        ) : (
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Group</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map(({ user, groupName }) => (
                                        <tr key={user.id}>
                                            <td>
                                                {getFullName(user)}
                                                <Link to={adminRoutes.user(user.id, 'courses-and-teams')}>
                                                    <BoxArrowUpRight />
                                                </Link>
                                            </td>
                                            <td>{groupName}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </UrlTabs.TabContent>
                </UrlTabs.Tab>
                <UrlTabs.Tab title={`Educators (${course?.user_assignments?.length ?? 0})`} eventKey="educators">
                    <UrlTabs.TabContent>
                        {!course?.user_assignments?.length ? (
                            <p className="text-muted">No educators assigned.</p>
                        ) : (
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Role</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {course.user_assignments.map((assignment) => (
                                        <tr key={assignment.id}>
                                            <td>
                                                {getFullName(assignment.user)}
                                                <Link to={adminRoutes.user(assignment.user.id, 'courses-and-teams')} className="ms-1">
                                                    <BoxArrowUpRight />
                                                </Link>
                                            </td>
                                            <td>{roleLabel[assignment.type] ?? assignment.type}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </UrlTabs.TabContent>
                </UrlTabs.Tab>
                <UrlTabs.Tab title={concepts.CustomerService} eventKey="customer-service">
                    <UrlTabs.TabContent>
                        <ArchiveToggleButton
                            baseUrl={backendEndpoints.deleteAdminCourse(courseId)}
                            deletedAt={course?.deleted_at}
                            resourceName="course"
                            onSuccess={reload}
                        />
                    </UrlTabs.TabContent>
                </UrlTabs.Tab>
            </UrlTabs>
        </Pages.Centred>
    );
}
